import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { apiClient, type ApiClientError } from '@/lib/api-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/efs/PageHeader';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

const NS = 'portal';

const BASIS_LABELS = ['market_middle', 'ef_estimate', 'dk_estimate', 'custom'] as const;

interface SupplierQuotationDto {
  supplier_quotation_id: string;
  monthly_batch_id: string;
  version: number;
  amount: string;
  basis_label: (typeof BASIS_LABELS)[number];
  notes: string | null;
  status: 'draft' | 'published' | 'superseded';
  source_first_valuation_id: string | null;
  published_at: string | null;
  created_at: string;
}

const fetchQuotations = async (batchId: string): Promise<SupplierQuotationDto[]> => {
  const resp = await apiClient.get<{ data: SupplierQuotationDto[] }>(
    `/monthly-batches/${batchId}/supplier-quotations`,
  );
  return resp.data.data;
};

/**
 * Platform management of the supplier-facing quotation (FR-006).
 *
 * Versions derive from the batch's finalized first valuation (amount + coarse
 * basis label, both overridable). PUBLISH makes a version the one the
 * supplier sees; the previously published version is superseded. The supplier
 * never sees this page — their read is the whitelist DTO.
 */
export const SupplierQuotationPage = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const { t } = useTranslation([NS, 'common']);
  const user = useCurrentUser();
  const queryClient = useQueryClient();

  const canManage = hasPermission(user, 'quotation.supplier.manage');
  const canPublish = hasPermission(user, 'quotation.supplier.publish');

  const list = useQuery({
    queryKey: ['supplier-quotations', batchId],
    queryFn: () => fetchQuotations(batchId!),
    enabled: !!batchId && canManage,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [basis, setBasis] = useState<string>('');
  const [notes, setNotes] = useState('');

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['supplier-quotations', batchId] });

  const create = useMutation({
    mutationFn: () =>
      apiClient.post(`/monthly-batches/${batchId}/supplier-quotations`, {
        amount: amount.trim() || null,
        basis_label: basis || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      setCreateOpen(false);
      setAmount('');
      setBasis('');
      setNotes('');
      void invalidate();
    },
  });

  const publish = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/monthly-batches/${batchId}/supplier-quotations/${id}/publish`, {}),
    onSuccess: () => void invalidate(),
  });

  const rows = list.data ?? [];

  if (!canManage) {
    return (
      <p className="text-sm text-efs-text-muted">
        {t('efs.common.errors.forbidden_title', { ns: 'common' })}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.portal.manage_quotation.title')}
        subtitle={t('efs.portal.manage_quotation.subtitle')}
        count={rows.length}
      />

      <div>
        <Button onClick={() => setCreateOpen(true)}>
          {t('efs.portal.manage_quotation.create')}
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-efs-text-muted">{t('efs.portal.manage_quotation.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('efs.portal.quotation.version')}</TableHead>
              <TableHead>{t('efs.portal.quotation.amount')}</TableHead>
              <TableHead>{t('efs.portal.quotation.basis')}</TableHead>
              <TableHead>{t('efs.portal.manage_quotation.status')}</TableHead>
              <TableHead>{t('efs.portal.quotation.published_at')}</TableHead>
              <TableHead className="text-right">
                {t('efs.portal.manage_quotation.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((q) => (
              <TableRow key={q.supplier_quotation_id}>
                <TableCell>v{q.version}</TableCell>
                <TableCell className="font-medium">{q.amount}</TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {t(`efs.portal.quotation.basis_labels.${q.basis_label}`)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={q.status === 'published' ? 'default' : 'outline'}>
                    {t(`efs.portal.manage_quotation.statuses.${q.status}`)}
                  </Badge>
                </TableCell>
                <TableCell>{q.published_at ?? '—'}</TableCell>
                <TableCell className="text-right">
                  {q.status === 'draft' && canPublish ? (
                    <Button
                      size="sm"
                      onClick={() => publish.mutate(q.supplier_quotation_id)}
                      disabled={publish.isPending}
                    >
                      {t('efs.portal.manage_quotation.publish')}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('efs.portal.manage_quotation.create')}</DialogTitle>
            <DialogDescription>{t('efs.portal.manage_quotation.create_hint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="sq-amount">{t('efs.portal.quotation.amount')}</Label>
              <Input
                id="sq-amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={t('efs.portal.manage_quotation.amount_placeholder')}
              />
            </div>
            <div className="space-y-1">
              <Label>{t('efs.portal.quotation.basis')}</Label>
              <Select value={basis} onValueChange={setBasis}>
                <SelectTrigger>
                  <SelectValue placeholder={t('efs.portal.manage_quotation.basis_default')} />
                </SelectTrigger>
                <SelectContent>
                  {BASIS_LABELS.map((b) => (
                    <SelectItem key={b} value={b}>
                      {t(`efs.portal.quotation.basis_labels.${b}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="sq-notes">{t('efs.portal.quotation.notes')}</Label>
              <Textarea
                id="sq-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            {create.isError ? (
              <p className="text-sm text-efs-danger">
                {(create.error as ApiClientError | undefined)?.message ??
                  t('efs.common.status.error', { ns: 'common' })}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={create.isPending}
            >
              {t('efs.common.actions.cancel', { ns: 'common' })}
            </Button>
            <Button type="button" onClick={() => create.mutate()} disabled={create.isPending}>
              {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {t('efs.portal.manage_quotation.save_draft')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierQuotationPage;
